/**
 * Offline state: a pure reducer over browser and service-worker events, so the
 * rules are unit-tested (state.test.ts). store.ts wraps it in the useOffline
 * store; serviceWorker.ts feeds it.
 */
import type { SwStatusMessage } from '@/sw/protocol';

/** The shape useOffline exposes. The presenter reads it: keep the fields stable. */
export interface OfflineState {
  /** navigator.onLine and the last /api/health ping both say the server is reachable. */
  online: boolean;
  /** A service worker controls this page, so it can answer fetches when the network cannot. */
  controlled: boolean;
  /** The service worker reports every precache entry outside /data cached: shell HTML, bundles, fonts, Cesium runtime, models, images. */
  shellCached: boolean;
  /** The service worker reports every bundled data file under /data cached (loader JSON, feed snapshots, elements, twin assets, timelines, knowledge). */
  dataCached: boolean;
  /** Map tiles in the service worker's tile cache (tiles browsed while online). */
  tilesCached: number;
  /** When the service worker last reported (ms since the epoch); null before its first report. */
  lastUpdate: number | null;
  /** A new service worker is installed and waiting: a reload, or applyUpdate, activates it. */
  updateReady: boolean;
  /** controlled, shellCached and dataCached: every mode can boot cold without the network. */
  offlineReady: boolean;
  /** navigator.onLine as the browser last reported it. */
  navigatorOnline: boolean;
  /** Outcome of the last /api/health ping; true until one fails. */
  reachable: boolean;
}

export type OfflineEvent =
  | { type: 'navigator'; online: boolean }
  | { type: 'health'; reachable: boolean }
  | { type: 'controller'; controlled: boolean }
  | { type: 'sw-status'; status: SwStatusMessage }
  | { type: 'update-ready' };

function derive(s: OfflineState): OfflineState {
  return {
    ...s,
    online: s.navigatorOnline && s.reachable,
    offlineReady: s.controlled && s.shellCached && s.dataCached,
  };
}

export function initialOfflineState(navigatorOnline: boolean, controlled: boolean): OfflineState {
  return derive({
    online: navigatorOnline,
    controlled,
    shellCached: false,
    dataCached: false,
    tilesCached: 0,
    lastUpdate: null,
    updateReady: false,
    offlineReady: false,
    navigatorOnline,
    reachable: true,
  });
}

export function reduceOffline(state: OfflineState, event: OfflineEvent): OfflineState {
  switch (event.type) {
    case 'navigator':
      return derive({ ...state, navigatorOnline: event.online });
    case 'health':
      return derive({ ...state, reachable: event.reachable });
    case 'controller':
      // A worker taking control is the waiting update (or the first install): nothing waits any more.
      return derive({
        ...state,
        controlled: event.controlled,
        updateReady: event.controlled ? false : state.updateReady,
      });
    case 'sw-status': {
      // Reports can cross in flight; an older one never overwrites a newer one.
      if (state.lastUpdate !== null && event.status.at < state.lastUpdate) return state;
      return derive({
        ...state,
        shellCached: event.status.shellCached,
        dataCached: event.status.dataCached,
        tilesCached: event.status.tilesCached,
        lastUpdate: event.status.at,
      });
    }
    case 'update-ready':
      return derive({ ...state, updateReady: true });
  }
}

export type OfflineBadgeState = 'online' | 'offline' | 'offline-ready';

/** ONLINE whenever the server is reachable; offline, READY when everything a cold boot needs is cached. */
export function badgeState(s: Pick<OfflineState, 'online' | 'offlineReady'>): OfflineBadgeState {
  if (s.online) return 'online';
  return s.offlineReady ? 'offline-ready' : 'offline';
}

export const BADGE_LABEL: Record<OfflineBadgeState, string> = {
  online: 'ONLINE',
  offline: 'OFFLINE',
  'offline-ready': 'OFFLINE READY',
};
